import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { Button } from "../common/button"
import { Filter } from "lucide-react"
import useTranslator from "@pma/hooks/use-translator"
import { Separator } from "../common/separator"
import { Input } from "../common/input"
import axios from "axios"
import { SERVER_URL } from "@pma/utils/api-routes"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { parseCookies } from "nookies"
import { useAuth } from "@pma/stores/auth-store"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../common/dropdown-menu"

interface FilterFilesProps {
  files: any[]
  setFileTypes: Dispatch<SetStateAction<string[]>>
  fileTypes: string[]
}

function FileFilter({ files, setFileTypes, fileTypes }: FilterFilesProps) {
  const { translate } = useTranslator()
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const { authConfig } = useAuth()
  const orgId = authConfig?.last_organization_id
  const [open, setOpen] = useState(false)

  const [filterList, setFilterList] = useState([])
  const [fileTypeList, setFileTypeList] = useState([])
  const [searchText, setSearchText] = useState<string>("")
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set([]))

  useEffect(() => {
    if (fileTypes.length == 0) setSelectedRows(new Set([]))
  }, [fileTypes])

  const handleGetFileTypes = async () => {
    try {
      const response = await axios.get(`${SERVER_URL}/files/type/getAll?organization_id=${orgId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })

      if (response.data) {
        const list = response.data.filter((type: string) => type != "")
        setFileTypeList(list)
      } else setFileTypeList([])
    } catch (error) {
    }
  }

  useEffect(() => {
    handleGetFileTypes()
  }, [])

  const handleCheckboxChange = (type: string) => {
    setSelectedRows((prev) => {
      const newSelected = new Set(prev)
      if (newSelected.has(type)) newSelected.delete(type)
      else newSelected.add(type)

      return newSelected
    })
  }

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const filtered = fileTypeList.filter((type: string) =>
        type.toLowerCase().includes(searchText.toLowerCase()),
      )
      setFilterList(filtered)
    }, 300) // Debounce delay in ms

    return () => clearTimeout(delayDebounce) // Cleanup timeout
  }, [searchText, fileTypeList])

  const handleSubmit = () => {
    const array = Array.from(selectedRows)
    setFileTypes(array)
    setOpen(!open)
    setSearchText("")
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant='outline'
          className='w-full sm:w-auto'
          size={"sm"}
          leftSection={<Filter size={14} />}
        >
          {translate("Filter")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <Input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={translate("Search Type")}
          onKeyDown={(e) => e.stopPropagation()}
        />
        <Separator className='my-2' />
        <div className='max-h-48 overflow-y-auto'>
          {filterList.map((option, index) => (
            <>
              <DropdownMenuCheckboxItem
                key={option}
                className='cursor-pointer truncate text-sm'
                checked={selectedRows.has(option)}
                onSelect={(e) => e.preventDefault()} // prevent menu from closing
                onCheckedChange={() => handleCheckboxChange(option)}
              >
                {option}
              </DropdownMenuCheckboxItem>
              {index != filterList.length - 1 ? <Separator className='my-0.5' /> : null}
            </>
          ))}
        </div>
        <div className='mt-2 border-t border-border pt-2'>
          <Button onClick={handleSubmit} size='sm' className='w-full'>
            {translate("Submit")}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default FileFilter
